import {
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  CreateEsignParams,
  DeleteEsignParams,
  ResignEsignParams,
  RevokeEsignParams,
  createEsign,
  deleteEsign,
  getEsignHistory,
  getEsignHistoryDetail,
  getFullEsignHistoryByOrder,
  resendEsign,
  revokeEsign,
} from '../apis/esign.api';
import { queryKey } from '../constants/queryClientKey';
import { DefaultErrorResponse } from '../interfaces';
import { sortObjectKeys } from '@vindr/utils/helpers/common.helper';

export function useGetEsignHistory(
  params: Parameters<typeof getEsignHistory>[0],
  options?: UseQueryOptions<Awaited<ReturnType<typeof getEsignHistory>>>
) {
  return useQuery(
    [queryKey.esign_history, params],
    () => getEsignHistory(params),
    options
  );
}

export function useGetEsignHistoryDetail(
  id: string,
  options?: UseQueryOptions<Awaited<ReturnType<typeof getEsignHistoryDetail>>>
) {
  return useQuery(
    [queryKey.esign_history, id],
    () => getEsignHistoryDetail(id),
    options
  );
}

export function useCreateEsign() {
  const queryClient = useQueryClient();

  return useMutation((data: CreateEsignParams) => createEsign(data), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.esign_history]);
      queryClient.invalidateQueries([queryKey.report_by_version_id]);
    },
    onError: (err: AxiosError<DefaultErrorResponse>) =>
      console.error('useCreateEsign Error:', err),
  });
}

export function useRevokeEsign() {
  const queryClient = useQueryClient();

  return useMutation((data: RevokeEsignParams) => revokeEsign(data), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.esign_history]);
      queryClient.invalidateQueries([queryKey.report_by_version_id]);
    },
    onError: (err: AxiosError<DefaultErrorResponse>) =>
      console.error('useRevokeEsign Error:', err),
  });
}

export function useResendEsign() {
  const queryClient = useQueryClient();

  return useMutation((data: ResignEsignParams) => resendEsign(data), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.esign_history]);
      queryClient.invalidateQueries([queryKey.report_by_version_id]);
    },
    onError: (err: AxiosError<DefaultErrorResponse>) =>
      console.error('useResendEsign Error:', err),
  });
}

export function useDeleteEsign() {
  const queryClient = useQueryClient();

  return useMutation((data: DeleteEsignParams) => deleteEsign(data), {
    onSuccess: () => queryClient.invalidateQueries([queryKey.esign_history]),
    onError: (err: AxiosError<DefaultErrorResponse>) =>
      console.error('useDeleteEsign Error:', err),
  });
}

export function useGetFullEsignHistoryByOrder(
  params: Parameters<typeof getFullEsignHistoryByOrder>[0],
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof getFullEsignHistoryByOrder>>
  >
) {
  return useQuery(
    options?.queryKey || [
      queryKey.full_esign_history_by_order,
      sortObjectKeys(params),
    ],
    () => getFullEsignHistoryByOrder(params),
    options
  );
}
